import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowLeft, ChevronDown, Globe, FileText, Trophy, Users, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import TournamentCard from "./TournamentCard";
import LeagueCard from "./LeagueCard";
import ChatIcon from "./ChatIcon";
import ChatScreen from "./ChatScreen";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useOrganizerProfile, OrganizerEvent } from "@/hooks/useOrganizerProfile";
import OrganizerReviewsSection from "./organizer/OrganizerReviewsSection";
import OrganizerPhotosSection from "./organizer/OrganizerPhotosSection";

interface OrganizerProfileProps {
  organizerId: string;
  onBack: () => void;
  onEventClick: (event: any) => void;
  onChatClick?: () => void;
  onChatCountChange?: (count: number) => void;
}

const OrganizerProfile = ({ organizerId, onBack, onEventClick, onChatClick, onChatCountChange }: OrganizerProfileProps) => {
  const { profile, loading, error } = useOrganizerProfile(organizerId);
  const [showChat, setShowChat] = useState(false);
  const [chatCount, setChatCount] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [eventFilter, setEventFilter] = useState<'tournaments' | 'leagues'>('tournaments');

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };


  const handleChatClick = () => {
    setShowChat(true);
    if (onChatClick) {
      onChatClick();
    }
  };

  const handleChatBack = () => {
    setShowChat(false);
    const newCount = chatCount + 1;
    setChatCount(newCount);
    if (onChatCountChange) {
      onChatCountChange(newCount);
    }
  };

  const handleEventClick = (event: OrganizerEvent) => {
    if (event.eventType === 'league') {
      // Pass same fields as ExploreLeagues for consistent league detail view
      onEventClick({
        id: event.id,
        title: event.title,
        sport: event.sport,
        date: event.date,
        endDate: event.endDate,
        time: event.time,
        start_time: event.start_time,
        end_time: event.end_time,
        game_days: event.game_days,
        location: event.location,
        participants: event.participants,
        maxParticipants: event.maxParticipants,
        registrationFee: event.registrationFee,
        start_date: event.date,
        end_date: event.endDate,
        entry_fee: event.registrationFee,
        max_participants: event.maxParticipants,
        event_type: 'league',
        isLeague: true,
        status: event.status,
        imageUrl: event.imageUrl,
        image_url: event.imageUrl,
        matchType: event.matchType,
        leagueFormat: event.leagueFormat,
        league_format: event.league_format,
        genderCategories: event.genderCategories,
        subcategories: event.subcategories,
        registrationDeadline: event.registrationDeadline,
        registration_deadline: event.registrationDeadline,
        total_weeks: event.total_weeks,
        matches_per_week: event.matches_per_week,
        address: event.address,
        postal_code: event.postal_code,
        organizer_id: event.organizer_id,
        organizer: profile?.name,
        rules: event.rules,
        description: event.description,
        categories: event.category ? [event.category] : [],
      });
    } else {
      // Tournament: keep existing behavior
      onEventClick({
        id: event.id,
        title: event.title,
        sport: event.sport,
        date: event.date,
        time: event.time,
        location: event.location,
        participants: event.participants,
        maxParticipants: event.maxParticipants,
        registrationFee: event.registrationFee,
        start_date: event.date,
        entry_fee: event.registrationFee,
        max_participants: event.maxParticipants,
        event_type: event.eventType,
        status: event.status,
        image_url: event.imageUrl,
        imageUrl: event.imageUrl,
      });
    }
  };

  // Show chat screen if chat is active
  if (showChat && profile) {
    return (
      <ChatScreen
        chatId={profile.id}
        partnerName={profile.name}
        partnerAvatar={profile.avatar}
        onBack={handleChatBack}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Cargando perfil del organizador...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Perfil del Organizador</h1>
        </div>
        <Card className="bg-card">
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">{error || 'No se encontró el organizador'}</p>
            <Button onClick={onBack} className="mt-4">Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const upcomingEvents = eventFilter === 'tournaments' 
    ? profile.upcomingTournaments 
    : profile.upcomingLeagues;


  return (
    <div className="space-y-8">
      {/* Organizer Info Card */}
      <Card className="bg-white mb-8">
        <CardContent className="p-6">
          {/* Row 1: Back + Name + Chat */}
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={onBack} className="p-2 -ml-2 shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h2 className="flex-1 text-xl sm:text-2xl font-bold text-foreground line-clamp-1 break-words leading-tight">{profile.name}</h2>
            <ChatIcon onClick={handleChatClick} />
          </div>

          {/* Row 2: Avatar + Stats */}
          <div className="flex items-center gap-4 mb-4">
            <Avatar className="h-20 w-20 shrink-0 ring-2 ring-[#e7d1b6]">
              <AvatarImage src={profile.avatar} alt={profile.name} />
              <AvatarFallback className="bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079] text-[#e7d1b6] font-semibold text-lg">
                {getInitials(profile.name)}
              </AvatarFallback>
            </Avatar>
            <div className="grid grid-cols-2 gap-3 flex-1">
              <div className="bg-[#31633f]/15 rounded-lg p-3 text-center border border-[#31633f]/30">
                <div className="text-base font-bold text-[#31633f]">{profile.tournamentsCount}</div>
                <div className="text-xs text-muted-foreground truncate">Torneos</div>
              </div>
              <div className="bg-[#31633f]/15 rounded-lg p-3 text-center border border-[#31633f]/30">
                <div className="text-base font-bold text-[#31633f]">{profile.leaguesCount}</div>
                <div className="text-xs text-muted-foreground truncate">Ligas</div>
              </div>
            </div>
          </div>
          {/* Description */}
          {profile.description && (
            <div className="mb-4 bg-muted/50 rounded-xl px-3 py-4 sm:px-4 border border-border">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-semibold text-foreground">Acerca del organizador</h4>
                </div>
                <div className="w-full">
                  <p className={`text-muted-foreground text-base leading-relaxed text-left ${
                    !showFullDescription && profile.description.length > 200 ? 'line-clamp-3' : ''
                  }`}>
                    {profile.description}
                  </p>
                  {profile.description.length > 200 && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="mt-3 text-primary hover:text-primary/80 text-sm font-medium transition-colors flex items-center gap-1"
                      >
                        {showFullDescription ? 'Ver menos' : 'Ver más'}
                        <ChevronDown className={`h-4 w-4 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Social Media Links */}
          {profile.website && (
            <div className="flex items-center gap-4">
              <a
                href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm">Sitio web</span>
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Events with Toggle Filter */}
      <Card className="bg-white">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <CardTitle className="text-xl font-bold text-foreground flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Próximos Eventos
            </CardTitle>
            <ToggleGroup 
              type="single" 
              value={eventFilter} 
              onValueChange={(value) => value && setEventFilter(value as 'tournaments' | 'leagues')}
              className="justify-center"
            >
              <ToggleGroupItem 
                value="tournaments" 
                className="flex items-center gap-2 px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Trophy className="h-4 w-4" />
                Torneos ({profile.upcomingTournaments.length})
              </ToggleGroupItem>
              <ToggleGroupItem 
                value="leagues"
                className="flex items-center gap-2 px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <Users className="h-4 w-4" />
                Ligas ({profile.upcomingLeagues.length})
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardHeader>
        <CardContent>
          {upcomingEvents.length > 0 ? (
            <div className="space-y-4">
              {upcomingEvents.map((event) => (
                eventFilter === 'leagues' ? (
                  <LeagueCard
                    key={event.id}
                    league={{
                      id: event.id,
                      title: event.title,
                      organizer: profile.name,
                      sport: event.sport,
                      date: event.date,
                      endDate: event.endDate,
                      location: event.location,
                      registrationFee: event.registrationFee,
                      imageUrl: event.imageUrl,
                      matchType: event.matchType,
                      leagueFormat: event.leagueFormat,
                      genderCategories: event.genderCategories,
                      subcategories: event.subcategories,
                      registrationDeadline: event.registrationDeadline,
                      total_weeks: event.total_weeks,
                      matches_per_week: event.matches_per_week,
                      maxParticipants: event.maxParticipants,
                      participants: event.participants,
                      address: event.address,
                      postal_code: event.postal_code,
                    }}
                    onLeagueClick={() => handleEventClick(event)}
                  />
                ) : (
                  <TournamentCard
                    key={event.id}
                    tournament={{
                      id: event.id,
                      title: event.title,
                      organizer: profile.name,
                      sport: event.sport,
                      date: event.date,
                      location: event.location,
                      registrationFee: event.registrationFee,
                      imageUrl: event.imageUrl
                    }}
                    onTournamentClick={() => handleEventClick(event)}
                  />
                )
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                No hay {eventFilter === 'tournaments' ? 'torneos' : 'ligas'} próximos
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviews & Ratings */}
      <OrganizerReviewsSection organizerId={profile.userId} />


    </div>
  );
};

export default OrganizerProfile;
